// Bank account detail: one account rebuilt from the account list (there is
// no single-account read endpoint). The page is read in order: the account
// summary and its balance alerts, the statement check, the statement
// imports, then the transaction workbench. Each workbench line opens a focused review drawer —
// line facts, categorize (posts a journal), link a posted journal, and save
// a rule for future imports. Open invoices and bills are suggestions;
// collecting or paying them stays on those documents.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  type AccountSuggestion,
  applyBankRules,
  autoReconcile,
  type BankAccount,
  type BankMatch,
  type BankTransactionLine,
  bankAccountIsActive,
  bankAccountTypeLabel,
  categorizeBankTransaction,
  type ChartAccount,
  createBankRule,
  getLowBalanceAlerts,
  importStatement,
  listBankAccounts,
  listBankTransactionMatches,
  listBankTransactions,
  listChartAccounts,
  listStatementImports,
  matchBankTransaction,
  previewStatementImport,
  type StatementContentEncoding,
  type StatementImport,
  type StatementPreview,
  suggestAccounts,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";
import {
  APPLY_RULES_LABEL,
  APPLY_RULES_RUNNING_LABEL,
  APPLY_RULES_TITLE,
  AUTO_MATCH_CONFIDENCE_THRESHOLD,
  AUTO_MATCH_LABEL,
  AUTO_MATCH_RUNNING_LABEL,
  AUTO_MATCH_TITLE,
} from "./match-copy";
import { resolveQuickChoices } from "./quick-choice";
import { readStatementFile } from "./read-statement-file";
import { PdfStatementReview } from "./PdfStatementReview";
import { StatementCheck } from "./StatementCheck";

const DISPLAY_CURRENCY = "USD";

// Binary workbooks stay base64; the paste box is only for text formats.
const STATEMENT_UPLOAD_ACCEPT =
  ".pdf,.xls,.xlsx,.csv,.tsv,.qif,.ofx,.qfx,.camt052,.camt053,.camt054,.mt940,.mt942,.bai2,application/pdf,text/csv,text/tab-separated-values,text/plain,application/x-ofx,application/vnd.intu.qfx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function formatDay(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function lineStatus(line: BankTransactionLine) {
  if (line.status === "reconciled") {
    return <StatusDot tone="success" label="Reconciled" />;
  }
  if (line.status === "excluded") {
    return <StatusDot tone="idle" label="Excluded" />;
  }
  return <StatusDot tone="idle" label="Unreconciled" />;
}

type ReviewConfirmation =
  | { kind: "categorize"; line: BankTransactionLine; accountId: string }
  | { kind: "link"; line: BankTransactionLine; match: BankMatch }
  | { kind: "rule"; line: BankTransactionLine; suggestion: AccountSuggestion };

export function BankAccountDetailPage() {
  const { accountId } = useParams({ strict: false }) as { accountId: string };
  const [account, setAccount] = useState<BankAccount | null>(null);
  const [transactions, setTransactions] = useState<BankTransactionLine[] | null>(
    null,
  );
  const [imports, setImports] = useState<StatementImport[] | null>(null);
  const [chartAccounts, setChartAccounts] = useState<ChartAccount[]>([]);
  const [lowNote, setLowNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  const [content, setContent] = useState("");
  // An extensionless name lets the API sniff pasted OFX/QIF/CAMT as well as CSV.
  const [fileName, setFileName] = useState("statement");
  const [contentEncoding, setContentEncoding] =
    useState<StatementContentEncoding>("text");
  const [sheetName, setSheetName] = useState("");
  const [reading, setReading] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<StatementPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [matching, setMatching] = useState(false);
  const [tab, setTab] = useState("unmatched");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [quickHint, setQuickHint] = useState<string | null>(null);

  const [reviewLineId, setReviewLineId] = useState<string | null>(null);
  const [reviewAccountId, setReviewAccountId] = useState("");
  const [reviewMatches, setReviewMatches] = useState<BankMatch[] | null>(null);
  const [reviewMatchesLoading, setReviewMatchesLoading] = useState(false);
  const [reviewMatchesError, setReviewMatchesError] = useState<string | null>(
    null,
  );
  const [reviewSuggestions, setReviewSuggestions] = useState<
    AccountSuggestion[] | null
  >(null);
  const [reviewSuggestLoading, setReviewSuggestLoading] = useState(false);
  const [reviewSuggestError, setReviewSuggestError] = useState<string | null>(
    null,
  );
  const [reviewAttempt, setReviewAttempt] = useState(0);
  const [reviewNotice, setReviewNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<ReviewConfirmation | null>(
    null,
  );

  const reviewLine =
    reviewLineId === null
      ? null
      : ((transactions ?? []).find((line) => line.id === reviewLineId) ?? null);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([
      listBankAccounts(),
      listBankTransactions(accountId),
      listStatementImports(accountId),
      getLowBalanceAlerts(),
      listChartAccounts(),
    ])
      .then(([rows, lines, batches, low, chart]) => {
        const found = rows.find((row) => row.id === accountId) ?? null;
        setAccount(found);
        setMissing(!found);
        setTransactions(lines ?? []);
        setImports(batches ?? []);
        setChartAccounts(chart ?? []);
        setSelectedIds(new Set());
        const alert = (low.alerts ?? []).find(
          (entry) => entry.bank_account_id === accountId,
        );
        setLowNote(
          alert
            ? `This account holds ${money(alert.balance_cents)} — below the ${money(alert.threshold_cents)} warning level.`
            : null,
        );
      })
      .catch((cause: unknown) => setLoadError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(reload, [reload]);

  useEffect(() => {
    if (!reviewLine) return;
    let cancelled = false;
    setReviewMatches(null);
    setReviewMatchesError(null);
    setReviewMatchesLoading(true);
    listBankTransactionMatches(reviewLine.id)
      .then((rows) => {
        if (!cancelled) setReviewMatches(rows ?? []);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setReviewMatchesError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setReviewMatchesLoading(false);
      });

    setReviewSuggestions(null);
    setReviewSuggestError(null);
    setReviewSuggestLoading(true);
    suggestAccounts(accountId, reviewLine.description, 5)
      .then((rows) => {
        if (!cancelled) setReviewSuggestions(rows ?? []);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setReviewSuggestError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setReviewSuggestLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, reviewAttempt, reviewLine]);

  const submitPreview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!content.trim()) {
      setError(
        "Paste statement text first — the preview parses it without saving anything.",
      );
      return;
    }
    setPreviewing(true);
    setError(null);
    setNotice(null);
    try {
      const result = await previewStatementImport({
        file_name: fileName.trim() || "statement",
        content,
        content_encoding: contentEncoding,
        ...(sheetName.trim() ? { sheet_name: sheetName.trim() } : {}),
      });
      setPreview(result);
      if (!result || !Array.isArray(result.transactions)) {
        setError(
          "The preview came back in an unexpected shape — nothing was imported.",
        );
      }
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setPreviewing(false);
    }
  };

  const submitImport = async () => {
    if (!content.trim()) {
      setError("Paste statement text first.");
      return;
    }
    setImporting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await importStatement({
        bank_account_id: accountId,
        file_name: fileName.trim() || "statement",
        content,
        content_encoding: contentEncoding,
        ...(sheetName.trim() ? { sheet_name: sheetName.trim() } : {}),
      });
      const imported = result?.imported ?? 0;
      const duplicates = result?.duplicate_count ?? 0;
      setNotice(
        `Imported ${imported} line${imported === 1 ? "" : "s"} (${duplicates} duplicate${duplicates === 1 ? "" : "s"} skipped).`,
      );
      setPreview(null);
      setImportOpen(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setImporting(false);
    }
  };

  const runMatch = async (action: "auto" | "rules") => {
    setMatching(true);
    setError(null);
    setNotice(null);
    try {
      if (action === "auto") {
        const result = await autoReconcile(accountId, {
          confidence_threshold: AUTO_MATCH_CONFIDENCE_THRESHOLD,
        });
        setNotice(
          `Checked ${result?.checked ?? 0}, matched ${result?.reconciled ?? 0}, skipped ${result?.skipped ?? 0}.`,
        );
      } else {
        const result = await applyBankRules(accountId);
        setNotice(
          `Applied ${result?.applied ?? 0} rules, matched ${result?.reconciled ?? 0}.`,
        );
      }
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setMatching(false);
    }
  };

  const openReview = (line: BankTransactionLine) => {
    setReviewLineId(line.id);
    setReviewAccountId(line.account_id ?? "");
    setReviewNotice(null);
    setConfirmation(null);
    setQuickHint(null);
  };

  const closeReview = () => {
    setReviewLineId(null);
    setReviewAccountId("");
    setReviewNotice(null);
    setConfirmation(null);
    setQuickHint(null);
  };

  const performCategorize = async (
    line: BankTransactionLine,
    account_id: string,
  ) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await categorizeBankTransaction(line.id, { account_id });
      setNotice(`Categorized “${line.description}” and posted its journal.`);
      closeReview();
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
      setConfirmation(null);
    } finally {
      setBusy(false);
    }
  };

  const performLink = async (line: BankTransactionLine, match: BankMatch) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await matchBankTransaction(line.id, { journal_entry_id: match.id });
      setNotice(
        "Linked the line to that journal. The invoice or bill is unchanged.",
      );
      closeReview();
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
      setConfirmation(null);
    } finally {
      setBusy(false);
    }
  };

  const performRemember = async (
    line: BankTransactionLine,
    suggestion: AccountSuggestion,
  ) => {
    setBusy(true);
    setError(null);
    setReviewNotice(null);
    try {
      const result = await createBankRule({
        name: `Match “${line.description.slice(0, 60)}”`,
        priority: 100,
        match_field: "description",
        match_type: "equals",
        match_value: line.description,
        account_id: suggestion.account_id,
        auto_reconcile: true,
      });
      setReviewNotice(
        `Saved a rule${result?.id ? ` (${result.id.slice(0, 8)}…)` : ""} — future imports with this exact description are categorized automatically.`,
      );
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
      setConfirmation(null);
    }
  };

  if (loading && account === null && !missing && !loadError) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Bank account" description="Loading…" />
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      </div>
    );
  }

  if (missing) {
    return (
      <div className="grid gap-6">
        <PageHeader
          title="Bank account"
          description="This account id is not in the organization account list."
          actions={
            <Button asChild variant="secondary">
              <Link to="/banking">Back to accounts</Link>
            </Button>
          }
        />
        <EmptyState
          title="Account not found"
          reason="The account may belong to another organization — pick one from the accounts list."
          action={
            <Button asChild>
              <a href="/banking">Back to accounts</a>
            </Button>
          }
        />
      </div>
    );
  }

  if (loadError && account === null) {
    return (
      <div className="grid gap-6">
        <PageHeader
          title="Bank account"
          description="The account could not be loaded."
          actions={
            <Button asChild variant="secondary">
              <Link to="/banking">Back to accounts</Link>
            </Button>
          }
        />
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {loadError}
          <div className="mt-2">
            <Button type="button" variant="secondary" size="sm" onClick={reload}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const unmatched = (transactions ?? []).filter(
    (line) => line.status === "unreconciled",
  );
  const matched = (transactions ?? []).filter(
    (line) => line.status !== "unreconciled",
  );
  const activeLines = tab === "matched" ? matched : unmatched;
  const previewRows =
    preview && Array.isArray(preview.transactions) ? preview.transactions : [];
  const confirmCategorize =
    confirmation?.kind === "categorize" ? confirmation : null;
  const confirmLink = confirmation?.kind === "link" ? confirmation : null;
  const confirmRule = confirmation?.kind === "rule" ? confirmation : null;
  const accountName = account?.account_name ?? "this bank account";
  const accountLabel = (id: string) => {
    const found = chartAccounts.find((entry) => entry.id === id);
    return found ? `${found.code} — ${found.name}` : id;
  };
  const reviewButtonLabel = (line: BankTransactionLine) =>
    line.status === "unreconciled" ? "Review" : "View";

  const stillToMatchCents = unmatched.reduce(
    (sum, line) => sum + Math.abs(line.amount_cents),
    0,
  );
  const selectedLines = (transactions ?? []).filter((line) =>
    selectedIds.has(line.id),
  );
  const selectedCents = selectedLines.reduce(
    (sum, line) => sum + Math.abs(line.amount_cents),
    0,
  );
  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectableLines = activeLines;
  const allActiveSelected =
    selectableLines.length > 0 &&
    selectableLines.every((line) => selectedIds.has(line.id));
  const toggleAllActive = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allActiveSelected) {
        for (const line of selectableLines) next.delete(line.id);
      } else {
        for (const line of selectableLines) next.add(line.id);
      }
      return next;
    });
  };
  const quickDirection = (reviewLine?.amount_cents ?? 0) >= 0 ? "money_in" : "money_out";
  const reviewQuickChoices =
    reviewLine?.status === "unreconciled"
      ? resolveQuickChoices(chartAccounts, quickDirection)
      : [];

  return (
    <div className="grid gap-6">
      <PageHeader
        title={account?.account_name ?? "Bank account"}
        description={
          account
            ? `${humanizeLabel(bankAccountTypeLabel(account.account_type))} account`
            : undefined
        }
        actions={
          <>
            <StatusDot
              tone={account && bankAccountIsActive(account) ? "success" : "idle"}
              label={account && bankAccountIsActive(account) ? "Open" : "Closed"}
            />
            <Button asChild variant="secondary">
              <Link to="/banking">All accounts</Link>
            </Button>
          </>
        }
      />

      {error && !importOpen ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}
      {loadError ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {loadError}
          <div className="mt-2">
            <Button type="button" variant="secondary" size="sm" onClick={reload}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <section
        aria-label="Account summary"
        data-testid="banking-progress-summary"
        className="surface grid gap-4 rounded-2xl p-5"
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.08em] text-[var(--muted-foreground)] uppercase">
              Balance
            </p>
            <p className="text-2xl font-semibold tracking-tight tabular-nums">
              {money(account?.balance_cents ?? 0)}
            </p>
          </div>
          <p className="text-sm text-[var(--muted-foreground)]">
            {account
              ? humanizeLabel(bankAccountTypeLabel(account.account_type))
              : "Bank"}{" "}
            account ·{" "}
            {account && bankAccountIsActive(account) ? "Open" : "Closed"}
          </p>
        </div>
        <dl
          aria-label="Import progress"
          className="grid gap-3 border-t border-[var(--border)] pt-4 sm:grid-cols-3"
        >
          <div>
            <dt className="text-xs font-medium tracking-wide text-[var(--muted-foreground)] uppercase">
              Still to match
            </dt>
            <dd className="text-lg font-semibold tabular-nums">
              {unmatched.length} line{unmatched.length === 1 ? "" : "s"}
            </dd>
            <dd className="text-xs text-[var(--muted-foreground)] tabular-nums">
              {money(stillToMatchCents)} total
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium tracking-wide text-[var(--muted-foreground)] uppercase">
              Selected
            </dt>
            <dd className="text-lg font-semibold tabular-nums">
              {selectedLines.length} line{selectedLines.length === 1 ? "" : "s"}
            </dd>
            <dd className="text-xs text-[var(--muted-foreground)] tabular-nums">
              {money(selectedCents)} total
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium tracking-wide text-[var(--muted-foreground)] uppercase">
              Matched
            </dt>
            <dd className="text-lg font-semibold tabular-nums">
              {matched.length} line{matched.length === 1 ? "" : "s"}
            </dd>
          </div>
        </dl>
        {selectedIds.size > 0 ? (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear selection
            </Button>
          </div>
        ) : null}
        {lowNote ? (
          <p
            role="status"
            className="rounded-xl border border-[var(--status-warning-foreground)] p-3 text-sm"
          >
            {lowNote}
          </p>
        ) : null}
      </section>

      <StatementCheck accountId={accountId} onFinished={reload} />

      <section aria-label="Statement imports" className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold tracking-tight">
            Statement imports
          </h2>
          <Button type="button" onClick={() => setImportOpen(true)}>
            Import a statement
          </Button>
        </div>
        {(imports ?? []).length === 0 ? (
          <EmptyState
            title="No imports yet"
            reason="Choose Import a statement to upload or paste a file and preview its lines. Importing skips duplicates."
          />
        ) : (
          <div className="surface overflow-hidden rounded-2xl">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Imported</TableHead>
                    <TableHead className="text-right">Duplicates</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(imports ?? []).map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell className="font-medium">
                        {batch.file_name}
                      </TableCell>
                      <TableCell>{batch.format}</TableCell>
                      <TableCell>
                        {formatDay(batch.statement_start_date)} –{" "}
                        {formatDay(batch.statement_end_date)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {batch.imported_count}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {batch.duplicate_count}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          tone={
                            batch.status === "completed"
                              ? "success"
                              : batch.status === "failed"
                                ? "danger"
                                : "warning"
                          }
                          label={humanizeLabel(batch.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        {(imports ?? []).flatMap((batch) => batch.warnings ?? []).length > 0 ? (
          <ul className="grid gap-1 text-xs text-[var(--muted-foreground)]">
            {(imports ?? []).flatMap((batch) =>
              (batch.warnings ?? []).map((warning, index) => (
                <li key={`${batch.id}-${index}`}>
                  {batch.file_name}: {warning}
                </li>
              )),
            )}
          </ul>
        ) : null}

      </section>
      <Drawer
        open={importOpen}
        onOpenChange={(next) => {
          if (!reading && !previewing && !importing) setImportOpen(next);
        }}
        title="Import a statement"
        description="Upload or paste a statement, then preview it without saving. Import adds its lines to this bank account."
        className="w-[min(46rem,100%)]"
        footer={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-wrap">
            <Button
              type="button"
              variant="secondary"
              className="col-span-2"
              disabled={reading || previewing || importing}
              onClick={() => setImportOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="statement-import-form"
              variant="secondary"
              disabled={reading || previewing || importing || !content.trim()}
            >
              {previewing ? "Previewing…" : "Preview"}
            </Button>
            <Button
              type="button"
              disabled={reading || importing || previewing || !content.trim()}
              onClick={() => void submitImport()}
            >
              {importing ? "Importing…" : "Import"}
            </Button>
          </div>
        }
      >
        {error ? (
          <p role="alert" className="mb-3 text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <form
          id="statement-import-form"
          onSubmit={submitPreview}
          className="grid gap-3"
        >
          <label
            htmlFor="imp-upload"
            className="grid gap-1 text-xs font-medium"
          >
            Upload a statement file (PDF, XLS/XLSX, CSV, TSV, QIF, OFX/QFX, CAMT, MT940/942,
            BAI2) — or paste it below
            <input
              id="imp-upload"
              type="file"
              accept={STATEMENT_UPLOAD_ACCEPT}
              className="input"
              disabled={reading || previewing || importing}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setContent("");
                setPreview(null);
                setError(null);
                setSheetName("");
                setPdfFile(null);
                if (/\.pdf$/i.test(file.name) || file.type === "application/pdf") {
                  setFileName(file.name);
                  setContentEncoding("text");
                  setPdfFile(file);
                  return;
                }
                setReading(true);
                void readStatementFile(file)
                  .then(({ content: nextContent, contentEncoding: nextEncoding }) => {
                    setFileName(file.name);
                    setContentEncoding(nextEncoding);
                    setContent(nextContent);
                  })
                  .catch((cause: unknown) => setError(toErrorMessage(cause)))
                  .finally(() => setReading(false));
              }}
            />
          </label>
          {pdfFile ? <PdfStatementReview key={`${pdfFile.name}-${pdfFile.lastModified}`} file={pdfFile}
            onCancel={() => setPdfFile(null)} onPrepared={(csv) => {
              setFileName(`${pdfFile.name}.reviewed.csv`);
              setContentEncoding("text");
              setContent(csv);
              setPreview(null);
              setPdfFile(null);
            }} /> : null}
          <div className="flex flex-wrap items-end gap-3">
            <label
              htmlFor="imp-file"
              className="grid gap-1 text-xs font-medium"
            >
              File name
              <Input
                id="imp-file"
                value={fileName}
                onChange={(e) => {
                  setFileName(e.target.value);
                  setPreview(null);
                }}
                disabled={reading || previewing || importing || contentEncoding === "base64"}
              />
            </label>
          </div>
          {contentEncoding === "base64" ? (
            <label className="grid gap-1 text-xs font-medium" htmlFor="imp-sheet">
              Worksheet name (optional)
              <Input
                id="imp-sheet"
                value={sheetName}
                disabled={reading || previewing || importing}
                onChange={(e) => {
                  setSheetName(e.target.value);
                  setPreview(null);
                }}
              />
              <span>
                The first worksheet is used when blank. Preview the dates and
                amounts before importing. Maximum 2 MiB.
              </span>
            </label>
          ) : !pdfFile ? <label
            htmlFor="imp-content"
            className="grid gap-1 text-xs font-medium"
          >
            Statement text (filled in by the upload above, or paste OFX, QIF,
            CAMT, MT940, or BAI2 here directly)
            <textarea
              id="imp-content"
              className="input min-h-28 font-mono text-xs"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setPreview(null);
              }}
              placeholder={
                "date,description,amount\n2026-09-01,Acme invoice payment,4825.00"
              }
              disabled={reading || previewing || importing}
            />
          </label> : null}
          {preview ? (
            <div className="grid gap-2">
              <p className="text-sm text-[var(--muted-foreground)]">
                Preview: {preview.transaction_count} line
                {preview.transaction_count === 1 ? "" : "s"} as {preview.format}
                {preview.warnings.length > 0
                  ? ` · ${preview.warnings.length} warning${preview.warnings.length === 1 ? "" : "s"}`
                  : ""}
                . Nothing saved yet — press Import to keep these.
              </p>
              {preview.warnings.length > 0 ? (
                <ul className="grid gap-1 text-xs text-[var(--status-warning-foreground)]">
                  {preview.warnings.map((warning, index) => (
                    <li
                      key={`preview-warning-${index}-${warning.slice(0, 32)}`}
                    >
                      {warning}
                    </li>
                  ))}
                </ul>
              ) : null}
              {previewRows.length > 0 ? (
                <div className="surface overflow-hidden rounded-2xl">
                  <div className="table-scroll">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewRows.slice(0, 25).map((line, index) => (
                          <TableRow
                            key={`preview-line-${index}-${line.date}-${line.amount_cents}`}
                          >
                            <TableCell>{formatDay(line.date)}</TableCell>
                            <TableCell>{line.description}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {money(line.amount_cents)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : null}
              {preview.transaction_count > 25 ? (
                <p className="text-xs text-[var(--muted-foreground)]">
                  Showing the first 25 of {preview.transaction_count} lines.
                </p>
              ) : null}
            </div>
          ) : null}
        </form>
      </Drawer>

      <section aria-label="Transaction workbench" className="grid gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          Transaction workbench
        </h2>
        <p className="text-sm text-[var(--muted-foreground)]">
          Review a line to categorize it, link a posted journal, or save a rule
          for future imports. This list shows the latest 200 lines.
        </p>

        <div className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5">
          <Button
            type="button"
            variant="secondary"
            disabled={matching}
            onClick={() => void runMatch("auto")}
            title={AUTO_MATCH_TITLE}
          >
            {matching ? AUTO_MATCH_RUNNING_LABEL : AUTO_MATCH_LABEL}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={matching}
            onClick={() => void runMatch("rules")}
            title={APPLY_RULES_TITLE}
          >
            {matching ? APPLY_RULES_RUNNING_LABEL : APPLY_RULES_LABEL}
          </Button>
        </div>

        {(transactions ?? []).length === 0 ? (
          <EmptyState
            title="No bank lines yet"
            reason="Import a statement above. Each saved line appears here for review."
          />
        ) : (
          <Tabs
            label="Transaction state"
            value={tab}
            onValueChange={setTab}
            tabs={[
              {
                value: "unmatched",
                label: `Unmatched (${unmatched.length})`,
                hint: "Lines not yet categorized or linked to a journal",
              },
              {
                value: "matched",
                label: `Matched (${matched.length})`,
                hint: "Lines already reconciled against a journal",
              },
            ]}
          >
            {activeLines.length === 0 ? (
              <EmptyState
                title={
                  tab === "matched" ? "No matched lines" : "No unmatched lines"
                }
                reason={
                  tab === "matched"
                    ? "Categorize or link a line and it moves here."
                    : "Every line is matched. Import another statement to bring in more."
                }
              />
            ) : (
              <>
                <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
                  <div className="table-scroll">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <input
                              type="checkbox"
                              aria-label="Select all visible bank lines"
                              checked={allActiveSelected}
                              onChange={toggleAllActive}
                              disabled={selectableLines.length === 0}
                            />
                          </TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>
                            <span className="sr-only">Action</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeLines.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                aria-label={`Select line ${line.description}`}
                                checked={selectedIds.has(line.id)}
                                onChange={() => toggleSelected(line.id)}
                              />
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {formatDay(line.date)}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {line.description}
                              </div>
                              {line.reference ? (
                                <div className="text-xs text-[var(--muted-foreground)]">
                                  {line.reference}
                                </div>
                              ) : null}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {money(line.amount_cents)}
                            </TableCell>
                            <TableCell>{lineStatus(line)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => openReview(line)}
                              >
                                {reviewButtonLabel(line)}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="stack-list grid gap-3 min-[64rem]:hidden">
                  {activeLines.map((line) => (
                    <article
                      key={line.id}
                      className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                    >
                      <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            aria-label={`Select line ${line.description}`}
                            checked={selectedIds.has(line.id)}
                            onChange={() => toggleSelected(line.id)}
                          />
                          <span className="record-title">{line.description}</span>
                        </label>
                        <span className="font-medium tabular-nums">
                          {money(line.amount_cents)}
                        </span>
                      </div>
                      <CardField label="Date">{formatDay(line.date)}</CardField>
                      <CardField label="Status">{lineStatus(line)}</CardField>
                      <CardField label="Action">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => openReview(line)}
                        >
                          {reviewButtonLabel(line)}
                        </Button>
                      </CardField>
                    </article>
                  ))}
                </div>
              </>
            )}
          </Tabs>
        )}
      </section>

      <Drawer
        open={reviewLine !== null}
        onOpenChange={(open) => {
          if (!open) closeReview();
        }}
        title={reviewLine ? reviewLine.description : "Review line"}
        description="Categorizing posts a journal. Linking records the link only. A saved rule applies to future imports."
        className="w-[min(34rem,100%)]"
        footer={
          <Button type="button" variant="secondary" onClick={closeReview}>
            Close
          </Button>
        }
      >
        {reviewLine ? (
          <div className="grid gap-5">
            <dl className="grid gap-2 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[var(--muted-foreground)]">Date</dt>
                <dd>{formatDay(reviewLine.date)}</dd>
              </div>
              {reviewLine.reference ? (
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-[var(--muted-foreground)]">Reference</dt>
                  <dd>{reviewLine.reference}</dd>
                </div>
              ) : null}
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[var(--muted-foreground)]">Amount</dt>
                <dd className="font-medium tabular-nums">
                  {money(reviewLine.amount_cents)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[var(--muted-foreground)]">Status</dt>
                <dd>{lineStatus(reviewLine)}</dd>
              </div>
            </dl>

            {reviewNotice ? (
              <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-3 text-sm">
                {reviewNotice}
              </output>
            ) : null}

            {reviewLine.status === "unreconciled" ? (
              <section
                aria-label="Categorize the line"
                className="surface grid gap-3 rounded-2xl p-4"
              >
                <h3 className="text-sm font-semibold">Categorize</h3>
                {reviewQuickChoices.length > 0 ? (
                  <div className="grid gap-2">
                    <p className="text-xs font-medium text-[var(--muted-foreground)]">
                      What was this?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {reviewQuickChoices.map((choice) => (
                        <Button
                          key={choice.key}
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => {
                            setReviewAccountId(choice.account.id);
                            setQuickHint(choice.hint);
                          }}
                        >
                          {choice.label}
                        </Button>
                      ))}
                    </div>
                    {quickHint ? (
                      <p className="text-xs text-[var(--muted-foreground)]">
                        {quickHint}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <label
                  htmlFor={`line-account-${reviewLine.id}`}
                  className="grid gap-1 text-xs font-medium"
                >
                  Ledger account
                  <AccountPicker
                    id={`line-account-${reviewLine.id}`}
                    value={reviewAccountId}
                    onChange={(value) => {
                      setReviewAccountId(value);
                      setQuickHint(null);
                    }}
                    disabled={busy}
                  />
                </label>
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (!reviewAccountId) {
                      setError(
                        "Pick a ledger account before categorizing this line.",
                      );
                      return;
                    }
                    setConfirmation({
                      kind: "categorize",
                      line: reviewLine,
                      accountId: reviewAccountId,
                    });
                  }}
                >
                  Categorize
                </Button>
              </section>
            ) : (
              <section
                aria-label="Matched state"
                className="surface grid gap-2 rounded-2xl p-4"
              >
                <h3 className="text-sm font-semibold">Matched</h3>
                {reviewLine.journal_entry_id ? (
                  <Link
                    to="/journal/$entryId"
                    params={{ entryId: reviewLine.journal_entry_id }}
                    className="font-medium text-[var(--action)] underline"
                  >
                    Open the posted journal
                  </Link>
                ) : (
                  <p className="text-xs text-[var(--muted-foreground)]">
                    This line is already reconciled.
                  </p>
                )}
              </section>
            )}

            <section
              aria-label="Link a journal entry"
              className="grid gap-3"
            >
              <h3 className="text-sm font-semibold">Link a journal entry</h3>
              <p className="text-xs text-[var(--muted-foreground)]">
                Linking records the line as reconciled against a posted
                journal. It does not change an invoice or bill.
              </p>
              {reviewLine.status === "unreconciled" ? (
                reviewMatchesLoading ? (
                  <p className="text-sm text-[var(--muted-foreground)]">
                    Checking journals…
                  </p>
                ) : reviewMatchesError ? (
                  <div
                    role="alert"
                    className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
                  >
                    {reviewMatchesError}
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setReviewAttempt((n) => n + 1)}
                      >
                        Retry
                      </Button>
                    </div>
                  </div>
                ) : (reviewMatches ?? []).length === 0 ? (
                  <p className="text-sm text-[var(--muted-foreground)]">
                    No journal, invoice, or bill scored high enough.
                  </p>
                ) : (
                  <ul className="grid gap-2">
                    {(reviewMatches ?? []).map((match) => (
                      <li
                        key={`${match.kind}-${match.id}`}
                        className="flex flex-wrap items-center gap-2 text-xs"
                      >
                        <span>
                          {match.kind === "journal_entry"
                            ? "Journal"
                            : match.kind === "invoice"
                              ? "Invoice"
                              : "Bill"}{" "}
                          {match.reference ? `${match.reference} ` : ""}
                          {match.description} · {money(match.amount_cents)} ·{" "}
                          {match.confidence}
                        </span>
                        {match.kind === "journal_entry" ? (
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={busy}
                            onClick={() =>
                              setConfirmation({
                                kind: "link",
                                line: reviewLine,
                                match,
                              })
                            }
                          >
                            Link journal
                          </Button>
                        ) : null}
                        {match.kind === "invoice" ? (
                          <Link
                            to="/invoices/$invoiceId"
                            params={{ invoiceId: match.id }}
                            className="font-medium text-[var(--action)] underline"
                          >
                            Open invoice
                          </Link>
                        ) : null}
                        {match.kind === "bill" ? (
                          <Link
                            to="/bills/$billId"
                            params={{ billId: match.id }}
                            className="font-medium text-[var(--action)] underline"
                          >
                            Open bill
                          </Link>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )
              ) : (
                <p className="text-xs text-[var(--muted-foreground)]">
                  This line is already reconciled, so there is nothing to link.
                </p>
              )}
            </section>

            <section
              aria-label="Remember this choice"
              className="surface grid gap-3 rounded-2xl p-4"
            >
              <h3 className="text-sm font-semibold">Remember this choice</h3>
              <p className="text-xs text-[var(--muted-foreground)]">
                Save a rule so future statement imports with this exact
                description are categorized automatically. This does not change
                the current line or any existing line.
              </p>
              {reviewSuggestLoading ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Looking for accounts…
                </p>
              ) : reviewSuggestError ? (
                <div
                  role="alert"
                  className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
                >
                  {reviewSuggestError}
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setReviewAttempt((n) => n + 1)}
                    >
                      Retry
                    </Button>
                  </div>
                </div>
              ) : (reviewSuggestions ?? []).length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  No account suggestions for this description yet.
                </p>
              ) : (
                <ul className="grid gap-2">
                  {(reviewSuggestions ?? []).map((suggestion) => (
                    <li
                      key={suggestion.account_id}
                      className="flex flex-wrap items-center gap-2 text-xs"
                    >
                      <span>
                        <span className="mr-1 font-mono text-[var(--muted-foreground)]">
                          {suggestion.account_code}
                        </span>
                        {suggestion.account_name} · confidence{" "}
                        {suggestion.confidence} · {suggestion.match_count} past
                        match
                        {suggestion.match_count === 1 ? "" : "es"}
                      </span>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          setConfirmation({
                            kind: "rule",
                            line: reviewLine,
                            suggestion,
                          })
                        }
                        title="Save a rule so future imports with this exact description are categorized automatically"
                      >
                        Remember this choice
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>

      {confirmCategorize ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmation(null);
          }}
          title={`Post the journal for ${money(confirmCategorize.line.amount_cents)}?`}
          description={`This categorizes “${confirmCategorize.line.description}” in ${accountName} to ${accountLabel(confirmCategorize.accountId)} and posts the journal now.`}
        >
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setConfirmation(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() =>
                void performCategorize(
                  confirmCategorize.line,
                  confirmCategorize.accountId,
                )
              }
            >
              {busy ? "Posting…" : "Post journal"}
            </Button>
          </div>
        </Dialog>
      ) : null}

      {confirmLink ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmation(null);
          }}
          title="Link this journal entry?"
          description={`This marks “${confirmLink.line.description}” reconciled against ${confirmLink.match.reference ? `${confirmLink.match.reference} ` : ""}${confirmLink.match.description} (${money(confirmLink.match.amount_cents)}). The invoice or bill is unchanged.`}
        >
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setConfirmation(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => void performLink(confirmLink.line, confirmLink.match)}
            >
              {busy ? "Linking…" : "Link journal entry"}
            </Button>
          </div>
        </Dialog>
      ) : null}

      {confirmRule ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmation(null);
          }}
          title="Remember this choice for future imports?"
          description={`This saves a rule so future statement imports whose description is exactly “${confirmRule.line.description}” are categorized to ${confirmRule.suggestion.account_code} — ${confirmRule.suggestion.account_name} automatically. It does not change this line or any existing line.`}
        >
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setConfirmation(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() =>
                void performRemember(
                  confirmRule.line,
                  confirmRule.suggestion,
                )
              }
            >
              {busy ? "Saving…" : "Save rule"}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
